import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Grid,
  Typography,
  LinearProgress,
} from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';
import { Close } from '@mui/icons-material';
import CaptainImage from '../../../../assets/images/tour/captain_tour.svg';
import VoicePlayImage from '../../../../assets/images/tour/voice_play_button.svg';
import './customTourStyles.css';
import CaptainAudio from '../../../../assets/audio/Camp_captain.mp3';
import MasterAudio from '../../../../assets/audio/Camp_Master.mp3';
import { useRecoilState } from 'recoil';
import {
  currentStepsAtom,
  stepsEnabledAtom,
} from '../../../../atoms/camp.atom';
import { useAuth } from '../../../auth/Auth';

const CaptainTourModal = ({ showModal, setShowModal }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [stepsEnabled, setStepsEnabled] = useRecoilState(stepsEnabledAtom);
  const [currentStep, setCurrentStep] = useRecoilState(currentStepsAtom); // Track the current step
  const [isPlaying, setIsPlaying] = useState(false); // To track audio play/pause state
  const [progress, setProgress] = useState(0); // To track progress of audio
  const audioRef = useRef(null); // Ref for the audio element
  const [showStartButton, setShowStartButton] = useState(false);
  const { school } = useAuth();

  const onClose = () => {
    setShowModal(false);
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0; // Reset audio
    }
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const progressPercentage =
        (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(progressPercentage);
    }
  };

  useEffect(() => {
    if (!showModal) {
      setProgress(0); // Reset progress when modal is closed
    }
  }, [showModal]);

  const handleStartTour = () => {
    setShowModal(false);
    setCurrentStep(0);
    setStepsEnabled(true);
  };

  const handleSkip = () => {
    if (audioRef.current) {
      audioRef.current.pause(); // Stop the audio if it's playing
      audioRef.current.currentTime = 0; // Reset audio to start
    }
    setIsPlaying(false);
    setShowStartButton(true); // Show the "Start Tour" button
  };

  return (
    <Dialog
      open={showModal}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        style: { borderRadius: '15px', padding: '16px' },
      }}
    >
      {/* Close button */}
      <Button
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          minWidth: '0',
          padding: '5px',
        }}
      >
        <Close style={{ color: '#321b6d' }} />
      </Button>

      {/* Dialog Content */}
      <DialogContent>
        <Grid container direction="column" alignItems="center" spacing={3}>
          {/* Image */}
          <Grid item>
            <img
              src={CaptainImage}
              alt="Add Guardian"
              style={{
                width: isMobile ? '150px' : '200px',
                height: '200px',
                marginBottom: '10px',
              }}
            />
          </Grid>

          {/* Title */}
          <Grid item>
            <Typography
              variant="h6"
              align="center"
              fontWeight="bold"
              color="#321b6d"
            >
              Welcome Camp{' '}
              {school?.user_type === 'school' ? 'Master' : 'Captain'}!
            </Typography>
          </Grid>
          <Grid item>
            <Typography
              variant="body1"
              align="center"
              fontWeight="bold"
              color="#321b6d"
            >
              Let’s listen to your key responsibilities and features.
            </Typography>
          </Grid>

          {/* Play Button */}
          {!isPlaying && !showStartButton && (
            <Grid item>
              <img
                src={VoicePlayImage}
                alt="VoicePlay"
                onClick={handlePlayPause}
                style={{
                  width: isMobile ? '150px' : '200px',
                  height: '60px',
                  marginBottom: '10px',
                  cursor: 'pointer',
                }}
              />
            </Grid>
          )}

          {/* Audio Progress Bar */}
          {isPlaying && (
            <Grid item xs={12} style={{ width: '100%' }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#30D884', // Change the color of the progress bar
                  },
                }}
              />
              <Typography variant="caption" align="center">
                {audioRef.current?.currentTime.toFixed(2) || '00:00'} /{' '}
                {audioRef.current?.duration.toFixed(2) || '00:00'}
              </Typography>
            </Grid>
          )}

          {/* Audio element */}
          <audio
            ref={audioRef}
            src={school?.user_type === 'school' ? MasterAudio : CaptainAudio} // Replace with the actual audio file
            onTimeUpdate={handleTimeUpdate}
            onEnded={() => {
              setIsPlaying(false);
              setShowStartButton(true);
            }} // Reset play state when audio ends
          />

          {/* Skip Button */}
          {!showStartButton && isPlaying && (
            <Grid item>
              <Button
                variant="outlined"
                onClick={handleSkip}
                sx={{ marginTop: '10px', borderColor: 'red', color: 'red' }}
              >
                Skip
              </Button>
            </Grid>
          )}

          {/* Start Tour Button */}
          {showStartButton && (
            <Grid item width="100%" sx={{ maxWidth: '392px !important' }}>
              <button
                className="Yes_btn"
                style={{
                  marginTop: isMobile ? '16px' : '20px',
                  fontSize: isMobile ? '14px' : '16px',
                  width: '100%',
                }}
                onClick={handleStartTour}
              >
                Start the Tour
              </button>
            </Grid>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default CaptainTourModal;
